import { Dropdown as NextDropdown, Loading } from '@nextui-org/react';
import React, { Key, ReactNode, useEffect, useId, useMemo, useState } from 'react';

import { CheckIcon, ChevronIcon } from '../../assets';
import Badge from '../FilterBadge';
import HelpTooltip from '../HelpTooltip';
import {
  CheckboxIndicatorBox,
  Container,
  DropDownButton,
  DropdownError,
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemLeftContainer,
  DropDownItemText,
  DropDownMenu,
  DropDownValueText,
  Separator,
  StyledDropdown,
  Title,
} from './Dropdown.styles';
import useDropdown from './useDropdown';

export interface DropdownItemProps<T> {
  key?: string;
  text?: string;
  // Shown in the trigger once selected, when it should differ from the menu text.
  selectedText?: string;
  value?: T;
  description?: string;
  selected?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  rightIcon?: ReactNode;
  /** Groups consecutive items with the same value under it as a heading; items without one stay ungrouped */
  section?: string;
}

export type DropdownSelection = number | number[];

// react-stately's `Section`, typed for this menu: the root TypeScript that lint runs can't resolve its generic props
const DropdownSection = NextDropdown.Section as unknown as (props: {
  title: string;
  children: ReactNode;
}) => JSX.Element;

export interface DropDownProps<T = any> extends React.ComponentPropsWithoutRef<typeof Container> {
  title?: string;
  items?: DropdownItemProps<T>[];
  selectedIndex?: number;
  selectedIndexes?: number[];
  placeholder?: string;
  multiselect?: boolean;
  helpText?: string;
  disabled?: boolean;
  hideBorder?: boolean;
  disabledKeys?: Key[];
  customTrigger?: ReactNode;
  onSelectionChange?: (selection: DropdownSelection) => void;
  loading?: boolean;
  disallowEmptySelection?: boolean;
  error?: string;
  required?: boolean;
  showBadge?: boolean;
  placeholderIcon?: ReactNode;
  showSelectedValue?: boolean;
  autoMenuWidth?: boolean;
  putSelectedFirst?: boolean;
  descriptionOnBottom?: boolean;
  shouldShowDescriptionWithValue?: boolean;
  hideChevron?: boolean;
  showCheckboxIndicator?: boolean;
  /** Close the open menu when anything outside it scrolls, and keep wheel scrolling inside the menu */
  closeOnOutsideScroll?: boolean;
}

const Dropdown = (props: DropDownProps) => {
  const {
    items,
    title,
    selectedIndex,
    selectedIndexes,
    placeholder,
    multiselect,
    helpText,
    disabled,
    disabledKeys,
    customTrigger,
    hideBorder,
    disallowEmptySelection = !multiselect,
    onSelectionChange,
    loading = false,
    error = '',
    required = false,
    showBadge,
    placeholderIcon,
    showSelectedValue = true,
    putSelectedFirst = false,
    descriptionOnBottom = false,
    shouldShowDescriptionWithValue = false,
    hideChevron = false,
    showCheckboxIndicator = false,
    closeOnOutsideScroll = false,
    ...rest
  } = props;
  const {
    selected,
    setSelected,
    selectedValue,
    selectedIcon,
    disabledKeysLocal,
    buttonRef,
    buttonWidth,
    selectedDescription,
    selectedRightIcon,
  } = useDropdown(props);

  const [isOpen, setIsOpen] = useState(false);

  // NextUI re-positions an open menu on every ancestor scroll, so it drifts away from its trigger;
  // close it (opt-in) on any scroll that doesn't happen inside the menu itself.
  useEffect(() => {
    if (!closeOnOutsideScroll || !isOpen) return undefined;
    const closeOnScroll = (event: Event) => {
      if (event.target instanceof Element && event.target.closest('.nextui-popover-content-container')) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, [closeOnOutsideScroll, isOpen]);

  const onLocalSelectionChange = (keys: 'all' | Set<Key>) => {
    // react-aria collapses the selection to the literal 'all' once every item ends up checked
    // (not just from a dedicated "select all" control), resolve it to the full key set instead
    // of dropping the update, or a multiselect with few items silently stops reflecting clicks
    // the moment the last item gets checked.
    const resolvedKeys = keys === 'all' ? new Set(items.map((_, index) => index.toString())) : keys;

    setSelected(resolvedKeys);
    const nextIndexes = Array.from(resolvedKeys || []).map((key) => Number(key));
    onSelectionChange?.(multiselect ? nextIndexes : nextIndexes[0]);
  };

  const sortedItems = useMemo(() => {
    if (!putSelectedFirst || !selected) return items;

    const currentIndexes = Array.from(selected).map(Number);
    return [...items].sort((a, b) => {
      const aIsSelected = currentIndexes.includes(items.indexOf(a));
      const bIsSelected = currentIndexes.includes(items.indexOf(b));
      if (aIsSelected === bIsSelected) return 0;
      return aIsSelected ? -1 : 1;
    });
  }, [items, selected, putSelectedFirst]);

  const hasSections = items?.some((item) => item.section !== undefined);

  const dropDownComponent = useMemo(
    () => (
      <DropDownMenu
        width={!props.autoMenuWidth && buttonWidth}
        selectionMode={multiselect ? 'multiple' : 'single'}
        disallowEmptySelection={disallowEmptySelection}
        selectedKeys={selected}
        disabledKeys={disabledKeysLocal}
        onSelectionChange={onLocalSelectionChange}
        $showCheckboxIndicator={showCheckboxIndicator}
        $containScroll={closeOnOutsideScroll}
      >
        {groupBySection(sortedItems).flatMap(({ section, sectionItems }) => {
          const renderedItems = sectionItems.map((item) => {
            const originalIndex = items.indexOf(item);
            const nextItem = sortedItems[sortedItems.indexOf(item) + 1];
            // A section draws its own divider, so its last item draws none
            const hasSeparator = hasSections ? nextItem?.section === item.section : originalIndex !== items.length - 1;
            return (
              <NextDropdown.Item key={originalIndex.toString()} textValue={originalIndex.toString()}>
                <DropDownItemContainer $descriptionOnBottom={descriptionOnBottom}>
                  <DropDownItemLeftContainer>
                    {showCheckboxIndicator && (
                      <CheckboxIndicatorBox>
                        <CheckIcon strokeWidth={3} />
                      </CheckboxIndicatorBox>
                    )}
                    {item.icon}
                    <DropDownItemText>{item.text}</DropDownItemText>
                    {item?.rightIcon}
                  </DropDownItemLeftContainer>
                  <DropDownItemDescription>{item.description}</DropDownItemDescription>
                </DropDownItemContainer>
                {hasSeparator && <Separator />}
              </NextDropdown.Item>
            );
          });
          if (section === undefined) return renderedItems;
          return [
            <DropdownSection key={`section-${section}`} title={section}>
              {renderedItems}
            </DropdownSection>,
          ];
        })}
      </DropDownMenu>
    ),
    [
      buttonWidth,
      disallowEmptySelection,
      disabledKeysLocal,
      sortedItems,
      items,
      multiselect,
      onLocalSelectionChange,
      selected,
      selectedValue,
      showCheckboxIndicator,
      closeOnOutsideScroll,
      hasSections,
    ],
  );

  // The title labels the button for screen readers, followed by the selected value. The value text gets the id, since
  // NextUI replaces the button's own id
  const titleId = useId();
  const valueId = useId();

  return (
    <Container {...rest}>
      {title && (
        <Title id={titleId}>
          {title}
          {required && ' *'}
          {helpText && <HelpTooltip $helpText={helpText} />}
        </Title>
      )}
      <StyledDropdown
        error={error}
        isDisabled={disabled}
        isBordered={!hideBorder}
        // Only controlled when opting in, so other dropdowns keep NextUI's own open state
        {...(closeOnOutsideScroll && { isOpen, onOpenChange: setIsOpen })}
      >
        {customTrigger ? (
          <StyledDropdown.Trigger>{customTrigger}</StyledDropdown.Trigger>
        ) : (
          <DropDownButton
            aria-labelledby={title ? `${titleId} ${valueId}` : undefined}
            error={error}
            disabled={disabled || loading}
            ref={buttonRef}
            color={error ? 'error' : 'default'}
            isBordered={!hideBorder}
            iconRight={loading ? <Loading type="points-opacity" size="sm" /> : hideChevron ? <div /> : <ChevronIcon />}
          >
            <DropDownItemLeftContainer>
              {showSelectedValue ? selectedIcon : placeholderIcon}
              <DropDownValueText id={valueId}>
                {(showSelectedValue ? selectedValue || placeholder : placeholder) || ''}
                {shouldShowDescriptionWithValue && ` ${selectedDescription}`}
              </DropDownValueText>
              {showBadge && <Badge>1</Badge>}
              {selectedRightIcon}
            </DropDownItemLeftContainer>
          </DropDownButton>
        )}
        {dropDownComponent}
      </StyledDropdown>
      {error && <DropdownError>{error}</DropdownError>}
    </Container>
  );
};

// Consecutive items sharing a section; items without one form groups of their own, rendered ungrouped
const groupBySection = (items: DropdownItemProps<unknown>[]) =>
  items.reduce<{ section?: string; sectionItems: DropdownItemProps<unknown>[] }[]>((groups, item) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.section === item.section) lastGroup.sectionItems.push(item);
    else groups.push({ section: item.section, sectionItems: [item] });
    return groups;
  }, []);

export default Dropdown;
